import { useTheme } from '../../../contexts/ThemeContext';

export default function Industries() {
  const { isDark } = useTheme();

  const industries = [
    {
      title: 'Professional Services',
      description: 'Automation for law firms, accounting firms, and consultants.',
      icon: 'ri-briefcase-4-line',
      image: 'https://public.readdy.ai/ai/img_res/a8a5c9f0eae86795f039f10e81843b79.jpg',
    },
    {
      title: 'Financial Services & Fintech',
      description: 'AI systems that support client engagement, data insights, and operational workflows.',
      icon: 'ri-line-chart-line',
      image: 'https://readdy.ai/api/search-image?query=Abstract%20minimal%20financial%20technology%20visualization%20with%20clean%20data%20charts%20and%20glowing%20graph%20lines%20on%20a%20very%20dark%20near-black%20background%2C%20subtle%20electric%20blue%20highlights%2C%20ultra%20modern%20fintech%20aesthetic%2C%20no%20people%2C%20cinematic%20lighting%2C%20high%20contrast%2C%20professional%20corporate%20style&width=800&height=500&seq=fortixa-ind-fintech-v2&orientation=landscape',
    },
    {
      title: 'Home Services & Trades',
      description: 'Automation for scheduling, lead capture, dispatching, and customer follow-up.',
      icon: 'ri-home-gear-line',
      image: 'https://readdy.ai/api/search-image?query=Clean%20modern%20home%20services%20professional%20workspace%20with%20neatly%20arranged%20tools%20on%20a%20white%20surface%2C%20tablet%20showing%20scheduling%20app%2C%20bright%20natural%20lighting%2C%20minimal%20and%20organized%20aesthetic%2C%20white%20and%20light%20gray%20tones%2C%20no%20people%2C%20product%20photography%20style&width=800&height=500&seq=fortixa-ind-home-v2&orientation=landscape',
    },
    {
      title: 'Healthcare & Medical Practices',
      description: 'AI tools that streamline patient communication and operational efficiency.',
      icon: 'ri-heart-pulse-line',
      image: 'https://readdy.ai/api/search-image?query=Minimalist%20modern%20medical%20clinic%20interior%20with%20clean%20white%20surfaces%2C%20soft%20ambient%20lighting%2C%20digital%20health%20interface%20on%20a%20sleek%20screen%2C%20calm%20and%20professional%20healthcare%20environment%2C%20no%20people%2C%20architectural%20photography%2C%20neutral%20white%20and%20light%20gray%20palette&width=800&height=500&seq=fortixa-ind-health-v2&orientation=landscape',
    },
    {
      title: 'Real Estate & Property Services',
      description: 'Automation for lead management, property inquiries, and client communication.',
      icon: 'ri-building-2-line',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20modern%20real%20estate%20property%20exterior%20with%20clean%20architectural%20lines%2C%20minimalist%20design%2C%20large%20glass%20windows%2C%20soft%20natural%20daylight%2C%20luxury%20residential%20or%20commercial%20building%2C%20no%20people%2C%20architectural%20photography%2C%20neutral%20tones%20of%20white%20gray%20and%20stone&width=800&height=500&seq=fortixa-ind-realestate-v2&orientation=landscape',
    },
  ];

  const cardBg = isDark ? '#1a1f2e' : '#ffffff';
  const cardText = isDark ? '#F9FAFB' : '#111827';
  const cardSubText = isDark ? '#9CA3AF' : '#374151';
  const iconBg = isDark ? '#374151' : '#111827';

  return (
    <section
      id="industries"
      className="py-32 px-8 lg:px-12 transition-colors duration-300"
      style={{ background: isDark ? '#111827' : '#F3F4F6' }}
    >
      <div className="max-w-7xl mx-auto">
        <p
          className="text-xs font-semibold tracking-[0.25em] uppercase text-[#2563EB] mb-5 text-center"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          Industries We Serve
        </p>

        <h2
          className="text-4xl lg:text-5xl font-bold text-center leading-tight tracking-tight mb-6"
          style={{ fontFamily: 'Montserrat, sans-serif', color: cardText }}
        >
          Built for High-Value
          <br />
          Service Industries
        </h2>

        <p
          className="text-lg text-center max-w-2xl mx-auto mb-20 leading-relaxed"
          style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, color: cardSubText }}
        >
          We specialize in sectors where operational efficiency and client responsiveness directly impact revenue and growth.
        </p>

        {/* Featured first card */}
        <div
          className="group relative rounded-2xl overflow-hidden transition-all duration-500 cursor-pointer mb-6"
          style={{ background: cardBg }}
        >
          <div className="grid lg:grid-cols-2">
            <div className="p-12 lg:p-16 flex flex-col justify-center">
              <div
                className="w-12 h-12 flex items-center justify-center rounded-xl mb-8 group-hover:bg-[#2563EB] transition-colors duration-300"
                style={{ background: iconBg }}
              >
                <i className={`${industries[0].icon} text-xl text-white`}></i>
              </div>
              <h3
                className="text-2xl lg:text-3xl font-bold mb-4 tracking-tight"
                style={{ fontFamily: 'Montserrat, sans-serif', color: cardText }}
              >
                {industries[0].title}
              </h3>
              <p
                className="leading-relaxed mb-8"
                style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, color: cardSubText }}
              >
                {industries[0].description}
              </p>
              <div className="flex items-center gap-2 text-[#2563EB] text-sm font-semibold">
                <span style={{ fontFamily: 'Montserrat, sans-serif' }}>Explore solutions</span>
                <i className="ri-arrow-right-line"></i>
              </div>
            </div>
            <div className="relative h-64 lg:h-auto min-h-[320px]">
              <img
                src={industries[0].image}
                alt={industries[0].title}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>
            </div>
          </div>
        </div>

        {/* Remaining 4 cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {industries.slice(1).map((industry, index) => (
            <div
              key={index}
              className="group rounded-2xl overflow-hidden transition-all duration-500 cursor-pointer"
              style={{ background: cardBg }}
            >
              <div className="relative w-full h-52 overflow-hidden">
                <img
                  src={industry.image}
                  alt={industry.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
              <div className="p-8">
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl mb-5 group-hover:bg-[#2563EB] transition-colors duration-300"
                  style={{ background: iconBg }}
                >
                  <i className={`${industry.icon} text-base text-white`}></i>
                </div>
                <h3
                  className="text-xl font-bold mb-3 tracking-tight"
                  style={{ fontFamily: 'Montserrat, sans-serif', color: cardText }}
                >
                  {industry.title}
                </h3>
                <p
                  className="text-sm leading-relaxed"
                  style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, color: cardSubText }}
                >
                  {industry.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
